import React from 'react';
import { skills, experience, education, projects, certifications } from '../mock';
import { Badge } from './ui/badge';
import { Terminal, Cpu, Rocket, GraduationCap, Briefcase, CheckCircle2, Layers } from 'lucide-react';

function SceneHeading({ id, eyebrow, title, subtitle }) {
  return (
    <div className="mb-12 reveal">
      <div className="flex items-center gap-3 mb-4">
        <span className="mono text-[10px] tracking-[0.4em] text-cyan-400">SCENE {id}</span>
        <span className="h-px w-10 bg-cyan-400/40" />
        <span className="eyebrow">{eyebrow}</span>
      </div>
      <h2 className="display text-4xl md:text-6xl leading-tight text-cyan-50 holo-text">{title}</h2>
      {subtitle && <p className="mt-4 max-w-2xl text-cyan-100/75 text-base md:text-lg">{subtitle}</p>}
    </div>
  );
}

function SkillsScene() {
  return (
    <section id="scene-02" className="scene">
      <div className="scene-inner">
        <SceneHeading
          id="02"
          eyebrow="Neural Core"
          title="Skills & Expertise"
          subtitle="Data signals feeding Astra’s cortex — the stack I ship with, calibrated across research and production."
        />
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 card-holo reveal">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Terminal size={14} className="text-cyan-300" /><span className="mono text-[11px] tracking-[0.3em] text-cyan-300">CORE_STACK.LOG</span></div>
              <span className="chip">{skills.core.length} MODULES</span>
            </div>
            <div className="divider-line my-4" />
            <div className="flex flex-wrap gap-2">
              {skills.core.map((s) => (
                <span key={s.label} className="chip">
                  <span className="h-1 w-1 rounded-full bg-cyan-300" /> {s.label}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 card-holo reveal">
            <div className="flex items-center gap-2"><Cpu size={14} className="text-cyan-300" /><span className="mono text-[11px] tracking-[0.3em] text-cyan-300">DOMAIN_MATRIX</span></div>
            <div className="divider-line my-4" />
            <ul className="space-y-3">
              {skills.domains.map((d, i) => (
                <li key={d} className="flex items-center gap-3">
                  <span className="mono text-[10px] text-cyan-400/70 w-6">{String(i + 1).padStart(2, '0')}</span>
                  <span className="h-px flex-1 bg-cyan-400/20" />
                  <span className="text-cyan-100/85 text-sm">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function ExperienceScene() {
  return (
    <section id="scene-03" className="scene">
      <div className="scene-inner">
        <SceneHeading
          id="03"
          eyebrow="Chassis & Mechanics"
          title="Experience & Missions"
          subtitle="Where theory meets torque — the missions that shaped my robotics craft."
        />
        <div className="relative">
          {/* timeline spine */}
          <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/60 via-cyan-400/20 to-transparent" />
          <div className="space-y-8">
            {experience.map((e, idx) => (
              <div key={e.id} className="relative pl-12 md:pl-16 reveal">
                <div className="absolute left-2 md:left-4 top-2 h-5 w-5 rounded-full border border-cyan-400/60 bg-cyan-950/80 grid place-items-center">
                  <div className="h-2 w-2 rounded-full bg-cyan-300 pulse-dot" />
                </div>
                <div className="card-holo">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="mono text-[10px] tracking-[0.3em] text-cyan-400">MISSION {String(idx + 1).padStart(2, '0')}</div>
                      <h3 className="display text-xl md:text-2xl text-cyan-50 mt-1">{e.role}</h3>
                      <div className="text-cyan-200/80 text-sm mt-1 inline-flex items-center gap-2">
                        <Briefcase size={13} /> {e.org} · {e.location}
                      </div>
                    </div>
                    <span className="mono text-[11px] text-cyan-300/80 border border-cyan-400/25 rounded px-2 py-1">{e.period}</span>
                  </div>
                  <ul className="mt-4 space-y-2">
                    {e.bullets.map((b) => (
                      <li key={b.slice(0, 32)} className="text-cyan-100/85 text-sm md:text-[15px] leading-relaxed flex gap-3">
                        <span className="mt-2 h-px w-4 bg-cyan-400/60 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {e.tags.map((t) => (
                      <Badge key={t} variant="outline" className="border-cyan-400/30 bg-cyan-950/40 text-cyan-200 mono text-[10px] tracking-widest">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ArchiveScene() {
  return (
    <section id="scene-04" className="scene">
      <div className="scene-inner">
        <SceneHeading
          id="04"
          eyebrow="The Archive"
          title="Education, Projects & Credentials"
          subtitle="The long record — the learning, the lab work, and the public signals of competence."
        />

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center gap-2"><GraduationCap size={16} className="text-cyan-300" /><span className="mono text-[11px] tracking-[0.3em] text-cyan-300">EDUCATION</span></div>
            <div className="grid sm:grid-cols-1 gap-4">
              {education.map((ed) => (
                <div key={ed.degree} className="card-holo reveal">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="display text-lg text-cyan-50">{ed.degree}</div>
                      <div className="text-cyan-200/80 text-sm mt-1">{ed.school}</div>
                    </div>
                    <span className="mono text-[11px] text-cyan-300/80 border border-cyan-400/25 rounded px-2 py-1 whitespace-nowrap">{ed.period}</span>
                  </div>
                  <p className="text-cyan-100/75 text-sm mt-3 leading-relaxed">{ed.note}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-8"><Layers size={16} className="text-cyan-300" /><span className="mono text-[11px] tracking-[0.3em] text-cyan-300">FEATURED PROJECTS</span></div>
            <div className="grid md:grid-cols-2 gap-4">
              {projects.map((p) => (
                <div key={p.id} className="card-holo reveal">
                  <div className="flex items-center gap-2 mono text-[10px] tracking-[0.3em] text-cyan-400/80">
                    <Rocket size={12} /> {p.role.toUpperCase()}
                  </div>
                  <h4 className="display text-lg text-cyan-50 mt-2">{p.name}</h4>
                  <p className="text-cyan-100/80 text-sm mt-2 leading-relaxed">{p.summary}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {p.stack.map((s) => (
                      <span key={s} className="chip">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="card-holo reveal">
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-cyan-300" /><span className="mono text-[11px] tracking-[0.3em] text-cyan-300">CERTIFICATIONS</span></div>
              <div className="divider-line my-4" />
              <ul className="space-y-3">
                {certifications.map((c, i) => (
                  <li key={c} className="flex gap-3 items-start">
                    <span className="mono text-[10px] text-cyan-400/70 mt-1 w-6">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-cyan-100/85 text-sm">{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-holo reveal">
              <div className="flex items-center justify-between">
                <div className="mono text-[11px] tracking-[0.3em] text-cyan-300">SIGNAL OUT</div>
                <span className="chip">CHANNEL OPEN</span>
              </div>
              <div className="divider-line my-4" />
              <p className="text-cyan-100/80 text-sm">Looking for robotics and AI roles in the UK & EU. Happy to collaborate on autonomous systems, computer vision and human–robot interaction.</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <a href="mailto:pribinbenny471@gmail.com" className="btn-primary rounded-md px-4 py-2 mono text-[11px] tracking-[0.3em] uppercase">Hail Operator</a>
                <a href="/docs/pribin-benny-resume.pdf" target="_blank" rel="noreferrer" className="btn-ghost rounded-md px-4 py-2 mono text-[11px] tracking-[0.3em] uppercase">View Dossier</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Sections() {
  return (
    <>
      <SkillsScene />
      <ExperienceScene />
      <ArchiveScene />
    </>
  );
}
